import type { Object3D } from 'three';
import type { Action, ActionAt, EventInfo, EventName, InteractionRule } from './actions';
import type { AudioManager, PlayAt } from '../audio/audioManager';
import type { StateController } from '../state/stateController';
import type { PointerInteraction } from './pointer';

// A2 — resolveAt returns this when an authored hero anchor can't be resolved, so
// runActions skips the play instead of letting it fall through to a global,
// unattenuated sound. Distinct from `undefined` (no anchor = intentional global).
const UNRESOLVED = Symbol('unresolved-anchor');

export interface EngineDeps {
  audio: AudioManager;
  state: StateController;
  setCamera: (name: string) => void;
  pointer: PointerInteraction;
  rules: InteractionRule[];
  /** Look up an Object3D by its hero_id userData. Used to resolve action.at. */
  getObjectByHeroId: (heroId: string) => Object3D | undefined;
  /** Cassette → music-slot override + boombox → eject. Wired to the
   *  SoundscapeEngine, which owns the music channel. */
  setMusicOverride?: (assetId: string, at: PlayAt | undefined, volume?: number) => void;
  clearMusicOverride?: () => void;
  /** The generator gate (N8): rules marked `requiresPower` are inert while
   *  unpowered; the `power.on` action latches it. Absent → always powered
   *  (the gate simply isn't wired). */
  power?: { readonly powered: boolean; on(): void };
}

export class InteractionEngine {
  private firedLoad = false;

  constructor(private deps: EngineDeps) {
    deps.pointer.on('click', (info) => this.fire('click', info));
    deps.pointer.on('hoverIn', (info) => this.fire('hoverIn', info));
    deps.pointer.on('hoverOut', (info) => this.fire('hoverOut', info));
    // State swaps fire 'stateEnter' immediately so music / ambient / lights
    // can cross-fade as the transition starts, not after it finishes.
    deps.state.onTargetChange((newTarget) => {
      this.fire('stateEnter', { state: newTarget });
    });
  }

  /**
   * Arm the load event: first user gesture both resumes the AudioContext and
   * fires any `event: 'load'` rules. Call once after constructing the engine.
   * Awaits resume before firing so source.start() runs against a running
   * context — some browsers (notably Safari) don't reliably play otherwise.
   *
   * Also fires `stateEnter` for the initial state so a rule matching
   * `whenState: <initial>` plays its music on first gesture without the
   * user having to toggle past↔present first.
   */
  arm(): void {
    if (this.firedLoad) return;
    const onFirstGesture = async () => {
      this.firedLoad = true;
      try { await this.deps.audio.resume(); } catch { /* ignored */ }
      this.fire('load', {});
      this.fire('stateEnter', { state: this.deps.state.current });
      window.removeEventListener('pointerdown', onFirstGesture);
    };
    window.addEventListener('pointerdown', onFirstGesture, { once: true });
  }

  /** Run an action list directly, outside of any matched rule — e.g. a
   *  time-scheduled cue firing. `info` defaults to empty (no clicked object,
   *  so playFromUserData / heroIdFromUserData resolve to nothing). */
  run(actions: Action[], info: EventInfo = {}): void {
    this.runActions(actions, info);
  }

  private fire(event: EventName, info: EventInfo): void {
    // For stateEnter, whenState filters against the new target (info.state).
    // For everything else, against the controller's current — that's the
    // state the user is actually seeing right now.
    const stateForMatch = info.state ?? this.deps.state.current;
    for (const rule of this.deps.rules) {
      const m = rule.match;
      if (m.event !== event) continue;
      if (m.heroId && m.heroId !== info.heroId) continue;
      if (m.heroIdPrefix && (!info.heroId || !info.heroId.startsWith(m.heroIdPrefix))) continue;
      if (m.whenState && m.whenState !== stateForMatch) continue;
      // N8: electrical heroes don't answer in a dark shop.
      if (rule.requiresPower && this.deps.power && !this.deps.power.powered) continue;
      this.runActions(rule.actions, info);
    }
  }

  private warnedUnresolved = new Set<string>();
  private warnUnresolved(heroId: string): void {
    if (this.warnedUnresolved.has(heroId)) return;
    this.warnedUnresolved.add(heroId);
    console.warn('[engine] unresolved anchor heroId:', heroId, '— action skipped (not played globally)');
  }

  private resolveAt(at: ActionAt | undefined, info: EventInfo): PlayAt | undefined | typeof UNRESOLVED {
    if (!at) return undefined; // no anchor → intentional global
    if (at.position) return { position: at.position };
    if (at.heroId) {
      const obj = this.deps.getObjectByHeroId(at.heroId);
      if (obj) return { object: obj };
      // A2: authored hero anchor that doesn't resolve — warn + signal skip
      // rather than playing the sound everywhere, unattenuated.
      this.warnUnresolved(at.heroId);
      return UNRESOLVED;
    }
    if (at.heroIdFromUserData) {
      const ref = info.object?.userData[at.heroIdFromUserData];
      if (typeof ref === 'string') {
        const obj = this.deps.getObjectByHeroId(ref);
        if (obj) return { object: obj };
        this.warnUnresolved(ref);
        return UNRESOLVED;
      }
      // No clicked-object userData to read (e.g. a scheduled cue) — not an
      // authored-but-missing anchor, so leave it to play unanchored.
      return undefined;
    }
    return undefined;
  }

  private runActions(actions: Action[], info: EventInfo): void {
    for (const a of actions) {
      switch (a.kind) {
        case 'audio.play': {
          const at = this.resolveAt(a.at, info);
          if (at === UNRESOLVED) break; // A2: missing anchor — warned, don't play globally
          this.deps.audio.play(a.id, {
            loop: a.loop,
            volume: a.volume,
            fadeIn: a.fadeIn,
            channel: a.channel,
            exclusive: a.exclusive,
            at,
            detune: a.detune,
          });
          break;
        }
        case 'audio.playFromUserData': {
          const id = info.object?.userData[a.key];
          if (typeof id === 'string') {
            const at = this.resolveAt(a.at, info);
            if (at === UNRESOLVED) break; // A2: missing anchor — don't play globally
            this.deps.audio.play(id, {
              loop: a.loop,
              volume: a.volume,
              fadeIn: a.fadeIn,
              channel: a.channel,
              exclusive: a.exclusive,
              at,
            });
          } else {
            console.warn('[engine] audio.playFromUserData: no string at userData.' + a.key);
          }
          break;
        }
        case 'audio.stop':
          this.deps.audio.stop(a.id, a.fadeOut);
          break;
        case 'audio.stopChannel':
          this.deps.audio.stopChannel(a.channel, a.fadeOut);
          break;
        case 'music.override': {
          const assetId = a.id ?? (a.key ? info.object?.userData[a.key] : undefined);
          if (typeof assetId === 'string') {
            const at = this.resolveAt(a.at, info);
            // A2: a missing anchor still plays — the music slot is a single
            // diegetic track, not a room-filling loop hazard — but as
            // unanchored (undefined), never as a phantom-positioned source.
            this.deps.setMusicOverride?.(assetId, at === UNRESOLVED ? undefined : at, a.volume);
          } else {
            console.warn('[engine] music.override: no asset id at userData.' + a.key);
          }
          break;
        }
        case 'music.clear':
          this.deps.clearMusicOverride?.();
          break;
        case 'state.set':
          this.deps.state.setTarget(a.target);
          break;
        case 'power.on':
          this.deps.power?.on();
          break;
        case 'camera.setMode':
          this.deps.setCamera(a.mode);
          break;
        case 'log':
          console.log('[action]', a.message);
          break;
        case 'callback':
          a.fn(info);
          break;
        default:
          // S9: a hand/agent-authored action with an unknown kind would no-op
          // silently. Don't strict-validate actions (the vocabulary is evolving
          // and 'callback' is runtime-only) — just surface the stray kind.
          console.warn('[action] unknown kind — ignored:', (a as { kind?: unknown }).kind);
          break;
      }
    }
  }
}
